import { useInfiniteQuery } from "@tanstack/react-query";
import api from "../../../api";
import { toLectureAttedanceWithLectureAnswersFromJson } from "../../../domain-adapters/LectureAttendance";
import { LectureAttendanceWithLectureAnswers } from "../../../domains/LectureAttendance";
import { createError, HTTPErrors } from "../../../errors";
import { getNextPageParam } from "../../../helpers/ReactQueryHelper";
import ApiResponse from "../../../interfaces/ApiResponse";
import SectionInterface from "../../../interfaces/SectionInterface";

type ResponseData = ApiResponse<LectureAttendanceWithLectureAnswers[]>;

type Props = {
  section: SectionInterface;
  lectureSessionId: string;
};

export const useFetchLectureAnswers = ({
  section,
  lectureSessionId,
}: Props) => {
  const result = useInfiniteQuery<ResponseData, HTTPErrors>({
    queryKey: cacheKeyOf(section, lectureSessionId),
    queryFn: async ({ pageParam }) => {
      const response = await api.get(urlOf(section, lectureSessionId), {
        query: { page: pageParam },
      });
      if (response.ok) {
        const json = await response.json();
        return {
          meta: json.lectureAttendances.meta,
          data: json.lectureAttendances.data.map(
            (lectureAttendance: Record<string, unknown>) =>
              toLectureAttedanceWithLectureAnswersFromJson(lectureAttendance),
          ),
        };
      }
      throw await createError(response);
    },
    initialPageParam: 1,
    getNextPageParam,
  });
  const data = result.data?.pages.flatMap((page) => page.data);
  return { ...result, data };
};

const cacheKeyOf = (section: SectionInterface, lectureSessionId: string) => [
  "lectureAnswers",
  section.id,
  lectureSessionId,
];

const urlOf = (section: SectionInterface, lectureSessionId: string) =>
  `/api/v1/sections/${section.id}/lecture_sessions/${lectureSessionId}/lecture_answers`;
